/**
 * @fileoverview Componente Molecule para seleccionar y cambiar el estado de un recordatorio de cliente en el Inbox.
 * Permite visualizar el estado actual y cambiarlo mediante un dropdown, con confirmación y manejo de loading.
 * Cumple con Atomic Design y separación estricta de lógica y presentación (UI/Actions Layer).
 *
 * @module CustomerReminderStatusSelectorMC
 * @category Component - Molecule
 * @since 3.1.0
 * @version 3.1.1
 *
 * @typedef {Object} CustomerReminderStatusSelectorMCProps
 * @property {Object} reminder - Objeto recordatorio individual (con status, web_id, etc.)
 * @property {function} setCustomerReminders - Setter para actualizar la lista de recordatorios (Actions Layer)
 *
 * @component CustomerReminderStatusSelectorMC
 * @description
 * **Características:**
 * - Visualiza y permite cambiar el estado del recordatorio (scheduled, sent, cancelled, etc.)
 * - Muestra confirmación antes de cancelar
 * - Maneja loading y errores de negocio
 * - Cumple Atomic Design: Molecule
 * - Usa hooks especializados: useCustomerReminderStatusUI (UI Layer), useCustomerReminderStatusActions (Actions Layer)
 * - Debug tracking con useTraceUpdate
 *
 * @param {CustomerReminderStatusSelectorMCProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado del selector de estado
 *
 * @example
 * <CustomerReminderStatusSelectorMC reminder={reminder} setCustomerReminders={setCustomerReminders} />
 *
 * @see {@link useCustomerReminderStatusUI} Hook UI Layer para lógica visual
 * @see {@link useCustomerReminderStatusActions} Hook Actions Layer para lógica de negocio
 */

import React, { memo } from 'react';
import Alert from '../../../../components/shared/Alert';
import DropdownButton from '../../../components/DropdownButtonMC';
import LoadingMC from '../../../components/LoadingMC';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import useCustomerReminderStatusUI from '../../../hooks/inbox/ui/useCustomerReminderStatusUI';
import useCustomerReminderStatusActions from '../../../hooks/inbox/actions/useCustomerReminderStatusActions';

const CustomerReminderStatusSelectorMC = memo(({ reminder, setCustomerReminders }) => {
  // UI Layer: lógica visual y de handlers
  const {
    confirmAlertRef,
    alertTitle,
    setAlertTitle,
    reminderStatusLabel,
    containerClassNameStatus,
    reminderStatusOptions,
    alertMessage,
    handleCancel,
  } = useCustomerReminderStatusUI(reminder);

  // Actions Layer: operaciones de negocio
  const {
    loading,
    handleConfirm,
  } = useCustomerReminderStatusActions({
    reminder,
    setCustomerReminders,
    setAlertTitle,
    confirmAlertRef,
  });

  // Debug tracking de dependencias críticas
  useTraceUpdate('CustomerReminderStatusSelectorMC', {
    reminderId: reminder?.id,
    status: reminder?.status,
    setCustomerRemindersRef: setCustomerReminders
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  if (loading) return <LoadingMC loading={loading} spinnerClassName="!tw-w-4 !tw-h-4" />;

  return (
    <>
      <DropdownButton
        variant="TEXT_PLAIN"
        text={reminderStatusLabel}
        items={reminderStatusOptions}
        containerClassName={containerClassNameStatus}
        className={`!tw-h-[20px] !tw-min-h-0 !tw-py-1 !tw-px-1 ${containerClassNameStatus}`}
      />
      <Alert
        ref={confirmAlertRef}
        title={alertTitle}
        message={alertMessage}
        type="confirm"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
});

CustomerReminderStatusSelectorMC.displayName = 'CustomerReminderStatusSelectorMC';

export default CustomerReminderStatusSelectorMC;